<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LocaleFlagIcon from '@/components/common/LocaleFlagIcon.vue';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import { SUPPORTED_LOCALES, getLocaleMeta, resolveFlag } from '@/config/locale.js';
import { setI18nLocale } from '@/i18n/index.js';

const props = defineProps({
  /** 视觉样式：default 常规 / transparent 透明顶栏 / dark 深色底栏 */
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'transparent', 'dark'].includes(value),
  },
  /** 移动端菜单内使用纵向列表，避免嵌套下拉 */
  layout: {
    type: String,
    default: 'dropdown',
    validator: (value) => ['dropdown', 'list'].includes(value),
  },
  /** 下拉展开方向：bottom 向下 / top 向上（页脚等底部场景） */
  placement: {
    type: String,
    default: 'bottom',
    validator: (value) => ['bottom', 'top'].includes(value),
  },
});

const emit = defineEmits(['select']);

const { t, locale } = useI18n();
const open = ref(false);
const rootRef = ref(null);

const currentLocale = computed(() => getLocaleMeta(locale.value));

function toggleOpen() {
  open.value = !open.value;
}

function close() {
  open.value = false;
}

function selectLocale(nextLocale) {
  setI18nLocale(nextLocale);
  close();
  emit('select', nextLocale);
}

function onDocumentClick(event) {
  if (!rootRef.value?.contains(event.target)) close();
}

function onDocumentKeydown(event) {
  if (event.key === 'Escape') close();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
});

const triggerClass = computed(() => {
  if (props.variant === 'transparent') {
    return 'text-white/70 hover:text-white';
  }
  if (props.variant === 'dark') {
    return 'text-slate-300 hover:text-white';
  }
  return 'text-muted-foreground hover:text-foreground';
});

const menuClass = computed(() => {
  if (props.variant === 'dark') {
    return 'border-white/10 bg-[#0a1628] text-white shadow-lg shadow-black/25';
  }
  return 'border-border/70 bg-background text-foreground shadow-lg shadow-black/8';
});

const menuPositionClass = computed(() =>
  props.placement === 'top' ? 'bottom-full' : 'top-full',
);

const menuBridgeClass = computed(() =>
  props.placement === 'top' ? 'pb-2' : 'pt-2',
);

const menuTransitionName = computed(() =>
  props.placement === 'top' ? 'locale-menu-top' : 'locale-menu',
);
</script>

<template>
  <!-- 纵向列表：适合移动端菜单 -->
  <div v-if="layout === 'list'" class="grid gap-1" role="listbox" :aria-label="t('locale.switch')">
    <button
      v-for="item in SUPPORTED_LOCALES"
      :key="item.value"
      type="button"
      role="option"
      :aria-selected="locale === item.value"
      class="flex items-center justify-between gap-3 border-b border-border/40 px-1 py-3 text-sm font-medium transition-colors last:border-b-0"
      :class="
        locale === item.value
          ? 'text-primary'
          : 'text-muted-foreground hover:text-foreground'
      "
      @click="selectLocale(item.value)"
    >
      <span class="flex min-w-0 items-center gap-2.5">
        <LocaleFlagIcon :code="resolveFlag(item)" size="md" />
        <span>{{ item.label }}</span>
      </span>
      <MaterialIcon v-if="locale === item.value" name="check" class="text-base" />
    </button>
  </div>

  <!-- 下拉选择：桌面顶栏 / 页脚（pointer-events-auto：页脚外层会禁用指针，需自行恢复） -->
  <div v-else ref="rootRef" class="relative pointer-events-auto" @mouseleave="close">
    <button
      type="button"
      class="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium transition-[color,background-color,border-color] duration-300 ease-in-out sm:gap-1.5 sm:px-2.5 sm:text-[13px]"
      :class="triggerClass"
      :aria-label="t('locale.current', { language: currentLocale?.label ?? locale })"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click.stop="toggleOpen"
    >
      <LocaleFlagIcon
        v-if="currentLocale"
        :code="resolveFlag(currentLocale)"
        size="md"
      />
      <span class="max-w-[6rem] truncate">{{ currentLocale?.shortLabel ?? currentLocale?.label }}</span>
      <MaterialIcon
        name="expand_more"
        class="text-base transition-transform duration-200"
        :class="{ 'rotate-180': open }"
      />
    </button>

    <Transition :name="menuTransitionName">
      <div
        v-if="open"
        class="absolute right-0 z-50"
        :class="[menuPositionClass, menuBridgeClass]"
        @click.stop
      >
        <div
          class="min-w-[9.5rem] overflow-hidden rounded-lg border py-1"
          :class="menuClass"
          role="listbox"
          :aria-label="t('locale.switch')"
        >
          <button
            v-for="item in SUPPORTED_LOCALES"
            :key="item.value"
            type="button"
            role="option"
            :aria-selected="locale === item.value"
            class="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors"
            :class="
              locale === item.value
                ? variant === 'dark'
                  ? 'bg-white/10 font-semibold text-white'
                  : 'bg-muted font-semibold text-foreground'
                : variant === 'dark'
                  ? 'text-slate-300 hover:bg-white/5 hover:text-white'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            "
            @click="selectLocale(item.value)"
          >
            <span class="flex min-w-0 items-center gap-2.5">
              <LocaleFlagIcon :code="resolveFlag(item)" size="sm" />
              <span>{{ item.label }}</span>
            </span>
            <MaterialIcon v-if="locale === item.value" name="check" class="text-base" />
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.locale-menu-enter-active,
.locale-menu-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.locale-menu-enter-from,
.locale-menu-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

.locale-menu-top-enter-active,
.locale-menu-top-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.locale-menu-top-enter-from,
.locale-menu-top-leave-to {
  opacity: 0;
  transform: translateY(0.25rem);
}
</style>
